import {DocumentBase} from '.';
import {Platforms, ProcessingTypes, ShippingSpeed} from '../constants';
import {UniversalTimestamp} from './compat';
import {ViewBase} from './ViewBase';
import {defaultViewSelector, ViewSelector} from './ViewSelector';

export enum EquipmentAvailability {
  inStock = 'In Stock',
  outOfStock = 'Out of Stock',
  notAvailable = 'Not Available',
}

export enum EquipmentConnectionType {
  wifi = 'WiFi',
  dial = 'Dial',
  cellular = 'Cellular',
  ethernet = 'IP (Ethernet)',
  bluetooth = 'Smart Phone Bluetooth Connection with Data Service',
}

export enum EquipmentFileType {
  retail = 'Retail',
  restaurant = 'Restaurant',
}

export enum EquipmentViewableBy {
  none = 'None',
  all = 'All',
  merchant = 'Merchant',
  agent = 'Agent',
  erpUser = 'ERP User',
}

export enum EquipmentDurationType {
  monthly = 'Monthly',
  device = 'Device',
}

export enum VarSheetType {
  rno = 'RNO',
  rao = 'RAO',
  na = 'N/A',
  taoRao = 'TAO/RAO',
  taoRno = 'TAO/RNO',
}

export const VarSheetTypeHostCaptureDisabled: VarSheetType[] = [
  VarSheetType.rno,
  VarSheetType.rao,
  VarSheetType.na,
];

export const VarSheetTypeHostCaptureEnabled: VarSheetType[] = [
  VarSheetType.taoRao,
  VarSheetType.taoRno,
];

export enum EquipmentTipMode {
  tipAdjust = 'Tip Adjust',
  tipInTransaction = 'Tip in Transaction',
}

export enum EquipmentYesOrNo {
  yes = 'Yes',
  no = 'No',
}

export const EquipmentOtherName = 'Other';

export const defaultEquipmentOptions: EquipmentOptions = {
  connectionType: Object.values(EquipmentConnectionType).map(e => e as EquipmentConnectionType),
  fileType: Object.values(EquipmentFileType).map(e => e as EquipmentFileType),
  tipMode: Object.values(EquipmentTipMode).map(e => e as EquipmentTipMode),
  cashDiscount: Object.values(EquipmentYesOrNo).map(e => e as EquipmentYesOrNo),
  autoBatch: Object.values(EquipmentYesOrNo).map(e => e as EquipmentYesOrNo),
  contactless: Object.values(EquipmentYesOrNo).map(e => e as EquipmentYesOrNo),
};

export type EquipmentForScheduleABillingBrands = 'dejavoo' | 'valor';

export type EquipmentOptions = Partial<{
  connectionType: EquipmentConnectionType[];
  fileType: EquipmentFileType[];
  tipMode: EquipmentTipMode[];
  cashDiscount: EquipmentYesOrNo[];
  autoBatch: EquipmentYesOrNo[];
  contactless: EquipmentYesOrNo[];
  accessories: string[];
}>;

export type EquipmentDefaults = Partial<{
  connectionType: EquipmentConnectionType;
  fileType: EquipmentFileType;
  tipMode: EquipmentTipMode;
  cashDiscount: boolean;
  cashDiscountPercent: number;
  autoBatch: boolean;
  autoBatchTime: string;
  contactless: boolean;
  accessories: string[];
  shippingSpeed: ShippingSpeed;
}>;

export type EquipmentAdditionalField = {
  required?: boolean;
  defaultValue?: string;
  label: string;
  name: string;
  type: string;
};

export type Equipment = DocumentBase & {
  name: string;
  isGateway?: boolean;
  isEnabledScheduleABilling?: boolean;
  isVarSheet?: boolean;
  softwareName?: string;
  price: number;
  duration: EquipmentDurationType;
  programs: string[] | null;
  imageUrl: string;
  description: string;
  features: string[];
  processingTypes: ProcessingTypes[];
  platform: Platforms;
  availability?: EquipmentAvailability;
  options?: EquipmentOptions;
  defaults?: EquipmentDefaults;
  viewableBy: EquipmentViewableBy;
  placementType?: string;
  virtualSetupFee?: number;
  virtualMonthlyFee?: number;
  virtualTransactionFee?: number;
  createdAt: UniversalTimestamp;
  additionalFields?: EquipmentAdditionalField[];
  hostCapture?: boolean;
  varSheetType?: VarSheetType;
};

export enum VARSheetEquipmentTypes {
  AuthorizeNet = 'Authorize.net',
  DejavooP1 = 'Dejavoo P1',
  DejavooP3PINPad = 'Dejavoo P3 PIN Pad',
  DejavooQD2 = 'Dejavoo QD2',
  DejavooQD3PINPad = 'Dejavoo QD3 PIN Pad',
  DejavooQD4 = 'Dejavoo QD4',
  DejavooZ11 = 'Dejavoo Z11',
  DejavooZ6PINPad = 'Dejavoo Z6 PIN Pad',
  DejavooZ8 = 'Dejavoo Z8',
  DejavooZ9 = 'Dejavoo Z9',
  FluidPay = 'Fluid Pay',
  IDTechVP3350 = 'ID Tech VP3350',
  IngenicoDesk1500PINPad = 'Ingenico Desk 1500 PIN Pad',
  IngenicoDesk3500 = 'Ingenico Desk 3500',
  IngenicoICT220PINPad = 'Ingenico ICT220 PIN Pad',
  NMI = 'NMI',
  PAXA35PINPad = 'PAX A35 PIN Pad',
  PAXA80 = 'PAX A80',
  PAXA920 = 'PAX A920',
  PAXA920Pro = 'PAX A920 Pro',
  PAXQ25PINPad = 'PAX Q25 PIN Pad',
  PAXS300PINPad = 'PAX S300 PIN Pad',
  PAXS80 = 'PAX S80',
  SwipeSimple = 'SwipeSimple',
  SwipeSimpleB350 = 'SwipeSimple B350',
  TransIT = 'TransIT',
  ValorRCKT = 'Valor RCKT',
  ValorVL100 = 'Valor VL100',
  ValorVL110 = 'Valor VL110',
  ValorVP100 = 'Valor VP100',
  ValorVP300PINPad = 'Valor VP300 PIN Pad',
  ValorVP550 = 'Valor VP550',
  ValorVP800 = 'Valor VP800',
  ValorVT = 'Valor VT',
}

export type EquipmentView = ViewBase<Equipment> & {isOther: boolean};

export const selectEquipmentView: ViewSelector<EquipmentView, Equipment> = snapshot => {
  const equipment = {
    ...defaultViewSelector(snapshot),
  };

  const isOther = equipment.name === EquipmentOtherName;
  equipment.options = isOther ? defaultEquipmentOptions : equipment.options;

  return {...equipment, isOther};
};

export const getAvailableEquipmentsView = (equipmentViews: EquipmentView[]) => {
  return equipmentViews
    .filter(eq =>
      eq.isGateway ? true : !eq.availability || eq.availability === EquipmentAvailability.inStock
    )
    .sort((a, b) => {
      return a.isOther ? 1 : b.isOther ? -1 : a.name > b.name ? 1 : -1;
    });
};

export const isVarSheetOtherEquipment = (equipmentName: string | undefined) =>
  equipmentName === 'VAR Sheet - Other';
export const isOtherNotListedEquipment = (equipmentName: string | undefined) =>
  equipmentName === 'Other - Not Listed';

export const isVarSheetTgsSetUp = (equipmentName: string | undefined) =>
  equipmentName === 'VAR Sheet + TGS Set Up';
